import React, { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import "@adyen/adyen-web/styles/adyen.css";
import { REDIRECT_RESULT_PARAMETER } from "./paymentPage.constants";
import { NO_ORDER_SUMMARY } from "./paymentPageCheckout.configuration";
import { PAGE_VIEW } from "./paymentPage.views";
import { NO_DROPIN, loadPaymentPage, openPaymentFormAgain, startPageStatePolling } from "./paymentPage.flow";
import { usePaymentPageDropinCallbacks } from "./usePaymentPageDropinCallbacks";
import PaymentPageLayout from "./PaymentPageLayout";

const NO_REDIRECT_RESULT = Object.freeze({ isPresent: false });

function readRedirectResult(searchParameters) {
    const redirectResult = searchParameters.get(REDIRECT_RESULT_PARAMETER);
    if (redirectResult === null) {
        return NO_REDIRECT_RESULT;
    }
    return Object.freeze({ isPresent: true, value: redirectResult });
}

function buildPaymentPageContext(pageState, dropinCallbacks) {
    return Object.freeze({
        paymentLinkId: pageState.paymentLinkId,
        redirectResult: readRedirectResult(pageState.searchParameters),
        updateView: pageState.updateView,
        updateOrderSummary: pageState.updateOrderSummary,
        dropinContainer: pageState.dropinContainer,
        dropinInstance: pageState.dropinInstance,
        dropinCallbacks,
    });
}

function PaymentPage() {
    const { referenceId } = useParams();
    const [searchParameters] = useSearchParams();
    const [pageView, setPageView] = useState(PAGE_VIEW.LOADING);
    const [orderSummary, setOrderSummary] = useState(NO_ORDER_SUMMARY);
    const dropinContainer = useRef();
    const dropinInstance = useRef(NO_DROPIN);
    const pageContextReference = useRef();

    const dropinCallbacks = usePaymentPageDropinCallbacks(pageContextReference);

    function handleTryAgain() {
        openPaymentFormAgain(pageContextReference.current);
    }

    pageContextReference.current = buildPaymentPageContext(
        {
            paymentLinkId: referenceId,
            searchParameters,
            updateView: setPageView,
            updateOrderSummary: setOrderSummary,
            dropinContainer,
            dropinInstance,
        },
        dropinCallbacks
    );

    useEffect(
        function loadPageOnOpen() {
            loadPaymentPage(pageContextReference.current);
        },
        [referenceId]
    );

    useEffect(
        function startPollingWhileWaiting() {
            return startPageStatePolling(pageContextReference.current, pageView);
        },
        [pageView]
    );

    return (
        <PaymentPageLayout
            pageView={pageView}
            orderSummary={orderSummary}
            dropinContainer={dropinContainer}
            onTryAgain={handleTryAgain}
        />
    );
}

export default PaymentPage;
